import { AlertTriangle, WifiOff, RefreshCw } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { cn } from "@/utils/cn";

interface ErrorStateProps {
  title?: string;
  description?: string;
  variant?: "error" | "offline";
  onRetry?: () => void;
  className?: string;
}

/**
 * ErrorState — fallback padrão para falhas de carregamento (ex.: Football Data
 * API indisponível). Nunca deve "quebrar" a tela — sempre um aviso elegante
 * com opção de tentar novamente, conforme especificação.
 */
export function ErrorState({
  title,
  description,
  variant = "error",
  onRetry,
  className,
}: ErrorStateProps) {
  const Icon = variant === "offline" ? WifiOff : AlertTriangle;
  const defaultTitle = variant === "offline" ? "Sem conexão no momento" : "Algo não saiu como esperado";
  const defaultDescription =
    variant === "offline"
      ? "Mostrando os últimos dados disponíveis. Vamos sincronizar assim que a conexão voltar."
      : "Não foi possível carregar essas informações agora.";

  return (
    <div className={cn("flex flex-col items-center justify-center gap-3 rounded-2xl border border-border bg-muted/40 px-6 py-10 text-center", className)}>
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-warning/15 text-warning">
        <Icon className="h-6 w-6" strokeWidth={1.75} />
      </div>
      <div className="space-y-1">
        <p className="font-display text-sm font-semibold">{title ?? defaultTitle}</p>
        <p className="max-w-xs text-sm text-muted-foreground">{description ?? defaultDescription}</p>
      </div>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1">
          <RefreshCw className="h-4 w-4" />
          Tentar novamente
        </Button>
      )}
    </div>
  );
}
